import { useEffect, useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { Copy, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Doc } from "../../convex/_generated/dataModel";
import { AddRoomTile } from "@/components/home/add-room-tile";
import { Avatar, RowAction } from "@/components/settings/frame";
import { BoardChip, DeleteBoardDialog, EditRoomDialog } from "@/components/settings/rooms";
import { planStatus } from "@/components/settings/plan-status";
import { VisitsBlock } from "@/components/settings/visits";
import { Button } from "@/components/ui/button";
import { said } from "@/lib/errors";

// The home's page, where Settings opens: its name, who is in it (and the
// invite), its 3D visits, its rooms and themes.
export const Route = createFileRoute("/settings/")({
  // Links that still land on /settings: the deletion link, the email
  // confirmation, the old help anchor. Each goes to its page.
  beforeLoad: ({ location }) => {
    const search = location.search as Record<string, unknown>;
    if (typeof search.deleteAccount === "string") {
      throw redirect({ to: "/settings/delete", search: { deleteAccount: search.deleteAccount } });
    }
    if (typeof search.email === "string") {
      throw redirect({
        to: "/settings/profile",
        search: { email: search.email, error: typeof search.error === "string" ? search.error : undefined },
      });
    }
    if (location.hash === "help") throw redirect({ to: "/settings/help" });
  },
  component: HomePage,
});

function HomePage() {
  const { t, i18n } = useTranslation("settings");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const me = useQuery(api.auth.getCurrentUser);
  const workspace = useQuery(api.workspaces.getMyWorkspace);
  const members = useQuery(api.workspaces.getMembers);
  const data = useQuery(api.boards.listForMyWorkspace);
  const plan = useQuery(api.billing.getMyPlan);
  const renameWorkspace = useMutation(api.workspaces.rename);
  const createInvite = useMutation(api.workspaces.createInvite);

  const [renaming, setRenaming] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [editingRoom, setEditingRoom] = useState<Doc<"boards"> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Doc<"boards"> | null>(null);

  // Stripe sends the owner back here after a purchase.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Thank you. The plan updates here as soon as Stripe confirms the payment.");
      window.history.replaceState(null, "", "/settings");
    }
  }, []);

  if (!workspace || !data || me === undefined) {
    return <div className="h-64 animate-pulse rounded-md bg-paper-2" />;
  }

  const isOwner = workspace.ownerId === (me?._id as string | undefined);
  // A client of the team sees the home and its people, not its plan or rooms to manage.
  const isTeam = workspace.role === "team";
  // In a design studio, the plan follows the role's billing, rooms and themes its rooms.
  const seesPlan = isTeam && (!workspace.studio || workspace.studio.keys.includes("billing"));
  const canRooms = isTeam && workspace.can.rooms;
  const team = (members ?? []).filter((m) => m.role === "team");
  const owner = team.find((m) => m.isOwner);
  const rooms = data.boards.filter((b) => b.kind === "room");
  const themes = data.boards.filter((b) => b.kind === "freeform");
  const existingRoomTypes = new Set(
    rooms.filter((b) => b.roomType && !b.recipeId).map((b) => b.roomType!),
  );
  const existingRecipeIds = new Set(rooms.filter((b) => b.recipeId).map((b) => b.recipeId!));

  const planName = plan
    ? plan.plan === "studio"
      ? "Ours Studio"
      : plan.plan === "plus"
        ? "Ours Plus"
        : t("plan.free")
    : null;
  const status = plan ? planStatus(plan, i18n.language) : null;
  const line = isTeam
    ? [planName, status, isOwner ? t("home.youOwnIt") : null].filter(Boolean).join(" · ")
    : t("workspace.client");

  async function onRename(event: React.FormEvent) {
    event.preventDefault();
    const name = renaming?.trim();
    if (!name || name === workspace!.name) return setRenaming(null);
    try {
      await renameWorkspace({ name });
      toast.success(t("workspace.renamed"));
      setRenaming(null);
    } catch (error) {
      toast.error(said(error, t("workspace.renameFailed")));
    }
  }

  async function onInvite() {
    try {
      setInviteCode(await createInvite());
    } catch (error) {
      toast.error(said(error, t("workspace.invite.failed")));
    }
  }

  return (
    <div className="grid gap-12">
      <header className="grid gap-1.5">
        <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">{t("home.eyebrow")}</span>
        {renaming !== null ? (
          <form onSubmit={onRename} className="flex flex-wrap items-end gap-3">
            <input
              autoFocus
              aria-label={t("workspace.name")}
              value={renaming}
              onChange={(e) => setRenaming(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setRenaming(null);
              }}
              maxLength={60}
              className="min-w-0 flex-1 border-b border-ink bg-transparent pb-1 font-display text-5xl italic leading-[1.05] tracking-tight text-ink outline-none md:text-6xl"
            />
            <div className="flex gap-1 pb-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setRenaming(null)}>
                {tc("actions.cancel")}
              </Button>
              <Button type="submit" size="sm">
                {tc("actions.save")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
            <h1 className="font-display text-5xl leading-[1.05] tracking-tight text-ink [overflow-wrap:anywhere] md:text-6xl">
              <span className="italic">{workspace.name}</span>
              <span className="text-accent">.</span>
            </h1>
            {isOwner ? (
              <RowAction
                className="mb-2 text-ink-3"
                onClick={() => setRenaming(workspace.name)}
              >
                <Pencil />
                <span className="sr-only sm:not-sr-only">{t("home.rename")}</span>
              </RowAction>
            ) : null}
          </div>
        )}
        {line ? <p className="text-sm text-ink-3">{line}</p> : null}
      </header>

      <section className="grid gap-4">
        <SectionHead
          label={t("home.people")}
          aside={plan && isTeam ? t("home.ofLimit", { used: plan.memberCount, limit: plan.limits.members }) : null}
        />
        <ul className="flex flex-wrap gap-x-9 gap-y-4">
          {team.map((m) => (
            <li key={m.id} className="flex items-center gap-3">
              <Avatar name={m.name} email={m.email} me={m.isMe} className="size-11 text-sm" />
              <div className="min-w-0">
                <p className="truncate text-sm text-ink">{m.name ?? m.email ?? t("workspace.someone")}</p>
                <p className="text-xs text-ink-3">
                  {m.isOwner ? t("workspace.ownerBadge") : t("members.canEdit")}
                  {m.isMe ? ` · ${t("workspace.you")}` : ""}
                </p>
              </div>
            </li>
          ))}
          {isOwner ? (
            <li>
              <button
                type="button"
                onClick={onInvite}
                className="group flex items-center gap-3 rounded-md text-start text-ink-3 transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
              >
                <span className="inline-flex size-11 items-center justify-center rounded-full border border-dashed border-line-strong transition-colors group-hover:border-ink-3">
                  <Plus className="size-4" />
                </span>
                <span>
                  <span className="block text-sm">{t("home.invite")}</span>
                  <span className="block text-xs">{t("home.inviteHint")}</span>
                </span>
              </button>
            </li>
          ) : null}
        </ul>
        {inviteCode ? (
          <div className="flex items-center justify-between gap-3 border-y border-line py-3">
            <span className="font-display text-2xl tracking-[0.3em] text-ink num">{inviteCode}</span>
            <div className="flex gap-3">
              <RowAction onClick={onInvite}>{t("invite.newCode")}</RowAction>
              <RowAction
                onClick={async () => {
                  await navigator.clipboard.writeText(inviteCode);
                  toast.success(t("workspace.invite.copied"));
                }}
              >
                <Copy />
                {tc("actions.copy")}
              </RowAction>
            </div>
          </div>
        ) : null}
      </section>

      {seesPlan && plan ? <VisitsBlock plan={plan} ownerName={owner?.name ?? null} company={!!workspace.studio} /> : null}

      {canRooms ? (
        <section className="grid gap-4">
          <SectionHead label={t("home.rooms")} aside={String(rooms.length)} />
          <div className="flex flex-wrap gap-2">
            {rooms.map((board) => (
              <BoardChip
                key={board._id}
                board={board}
                onEdit={() => setEditingRoom(board)}
                onDelete={() => setConfirmDelete(board)}
              />
            ))}
            <AddRoomTile
              existingRoomTypes={existingRoomTypes}
              existingRecipeIds={existingRecipeIds}
              variant="chip"
            />
          </div>
          <p className="text-xs text-ink-3">{t("rooms.description")}</p>
        </section>
      ) : null}

      {canRooms && themes.length > 0 ? (
        <section className="grid gap-4">
          <SectionHead label={t("home.themes")} aside={String(themes.length)} />
          <div className="flex flex-wrap gap-2">
            {themes.map((board) => (
              <BoardChip
                key={board._id}
                board={board}
                onEdit={() => void navigate({ to: "/boards/$boardId", params: { boardId: board._id } })}
                onDelete={() => setConfirmDelete(board)}
              />
            ))}
          </div>
        </section>
      ) : null}

      <EditRoomDialog room={editingRoom} onClose={() => setEditingRoom(null)} />
      <DeleteBoardDialog board={confirmDelete} onClose={() => setConfirmDelete(null)} />
    </div>
  );
}

function SectionHead({ label, aside }: { label: string; aside?: string | null }) {
  return (
    <div className="flex items-end justify-between gap-4 border-b border-line-strong pb-2">
      <h2 className="text-2xs uppercase tracking-[0.18em] text-ink-3">{label}</h2>
      {aside ? <span className="text-2xs uppercase tracking-[0.18em] text-ink-4 num">{aside}</span> : null}
    </div>
  );
}
